/**
 * brand-theme — re-skin de la app en runtime con los colores de la Marca.
 *
 * La paleta por defecto (globals.css) es NEUTRA. Cuando existe una Marca con
 * `colorPrimario` / `colorAcento` válidos, fijamos esos colores como custom
 * properties inline en `document.documentElement` para pisar el default neutro:
 *   - --primary / --primary-foreground   ← colorPrimario
 *   - --brand-accent / --brand-accent-foreground ← colorAcento
 *
 * El foreground se elige automáticamente (negro o blanco) según la luminancia
 * del color de fondo para garantizar contraste legible.
 *
 * Los colores SEMÁNTICOS (estados, destructive, enviado/verde) NO se tocan acá:
 * viven fijos en globals.css y la marca jamás los pisa.
 *
 * Nada en este módulo debe lanzar: la marca es cosmética y no puede bloquear la app.
 */

/** Tokens de texto neutros (mismos que globals.css). */
const DARK_TOKEN = "#1a1a18"; // casi-negro editorial
const LIGHT_TOKEN = "#f7f6f1"; // papel claro

/** Valida un color hex de 6 dígitos: `#RRGGBB` (case-insensitive). */
export function isHex6(s: unknown): s is string {
  return typeof s === "string" && /^#[0-9a-fA-F]{6}$/.test(s);
}

/**
 * Devuelve el token de texto (oscuro o claro) con mejor contraste sobre `hex`.
 * Usa luminancia relativa sRGB (WCAG): fondos claros → texto oscuro, y viceversa.
 * Si `hex` no es válido, cae a texto claro (asume fondo oscuro tipo --primary).
 */
export function contrastColor(hex: string): string {
  if (!isHex6(hex)) return LIGHT_TOKEN;

  const r = Number.parseInt(hex.slice(1, 3), 16) / 255;
  const g = Number.parseInt(hex.slice(3, 5), 16) / 255;
  const b = Number.parseInt(hex.slice(5, 7), 16) / 255;

  const lin = (c: number): number =>
    c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;

  const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);

  // Umbral 0.5: fondo luminoso → texto oscuro; fondo apagado → texto claro.
  return luminance > 0.5 ? DARK_TOKEN : LIGHT_TOKEN;
}

interface BrandColors {
  colorPrimario?: string;
  colorAcento?: string;
}

/** Fija una custom property inline, o la remueve si el color es inválido. */
function applyPair(
  root: HTMLElement,
  bgProp: string,
  fgProp: string,
  color: string | undefined,
): void {
  if (isHex6(color)) {
    root.style.setProperty(bgProp, color);
    root.style.setProperty(fgProp, contrastColor(color));
  } else {
    // Remueve el override inline → vuelve al default neutro de globals.css.
    root.style.removeProperty(bgProp);
    root.style.removeProperty(fgProp);
  }
}

/**
 * Aplica (o resetea) el theme de marca sobre `document.documentElement`.
 *
 * - `colorPrimario` válido → pisa --primary / --primary-foreground.
 * - `colorAcento` válido   → pisa --brand-accent / --brand-accent-foreground.
 * - color inválido o ausente → remueve el override (cae al neutro de globals).
 * - `null` → resetea TODO a neutro (logout / sin marca).
 *
 * Seguro de llamar con `null`. Nunca lanza.
 */
export function applyBrandTheme(brand: BrandColors | null): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;

  applyPair(root, "--primary", "--primary-foreground", brand?.colorPrimario);
  applyPair(
    root,
    "--brand-accent",
    "--brand-accent-foreground",
    brand?.colorAcento,
  );
}
